import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { TextInput } from '../components/ui';

export function SignIn() {
  const { signIn, status } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') return <Navigate to="/" replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not sign in. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell">
      <aside className="auth-aside">
        <div className="row gap-6">
          <div className="brand-mark">K</div>
          <div className="brand-name" style={{ fontSize: 17 }}>
            Kortex HMS
          </div>
        </div>

        <div>
          <h1>One system from the purchase order to the payslip.</h1>
          <p>
            Restaurant billing with GST, stock that depletes itself from recipes, cost-led menu pricing,
            procurement, payroll and the front desk — across every property you run.
          </p>
          <div className="auth-points">
            <div>
              <span>▤</span>
              <span>
                <b>Billing that moves stock.</b> Settling a ticket raises the GST invoice and depletes every
                ingredient behind it.
              </span>
            </div>
            <div>
              <span>⇄</span>
              <span>
                <b>Procurement that reprices.</b> A goods receipt re-weights the moving average and updates
                plate costs.
              </span>
            </div>
            <div>
              <span>₹</span>
              <span>
                <b>Filing-ready GST.</b> B2B, B2CS and HSN summaries export straight out of the ledger.
              </span>
            </div>
          </div>
        </div>

        <div className="small" style={{ color: '#7fa9a4' }}>
          Multi-property · role-based access · full audit trail
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-card">
          <h2>Sign in</h2>
          <div className="sub">Use the account your manager set up for you.</div>

          <form onSubmit={submit} className="card">
            <div className="card-body col gap-16">
              {error ? <div className="form-error">{error}</div> : null}

              <TextInput
                label="Email"
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@property.in"
              />
              <TextInput
                label="Password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <button className="btn primary lg block" type="submit" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </div>
          </form>

          <div className="auth-hint">
            <b>Demo data</b> is seeded with <code>npm run db:seed</code>. Owner:{' '}
            <code>owner@kortexgrand.in</code> / <code>ChangeMe#2026</code>. Staff accounts (
            <code>manager@</code>, <code>cashier@</code>, <code>kitchen@</code>, <code>housekeeping@</code>,{' '}
            <code>stores@</code>, <code>frontdesk@</code>, <code>accounts@kortexgrand.in</code>) share the
            password <code>Staff#Demo2026</code> and each sees only what their role allows.
          </div>
        </div>
      </main>
    </div>
  );
}
